// Task #3822 — pannello "Progetti" nel drawer del workspace PA: lista dei
// progetti (con drill-in nel dettaglio) + creazione. Vive dentro il
// WorkspaceSidePanel (single source of truth desktop/mobile).
import React, { useState } from 'react';
import { FolderKanban, Loader2, Plus, Users, X } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { formatRelativeTime } from '../chatFormat';
import { usePaProjects, projectsApi } from './useProjects';
import { ProjectDetailPanel } from './ProjectDetailPanel';

export interface ProjectsPanelProps {
  /** Apre una conversazione (propria) del progetto nella chat live. */
  onSelectConversation: (id: string) => void;
  /** Crea una nuova conversazione dentro il progetto e la apre in chat. */
  onNewProjectConversation?: (projectId: string) => Promise<void> | void;
  currentUserId: string | null;
}

export const ProjectsPanel: React.FC<ProjectsPanelProps> = ({
  onSelectConversation,
  onNewProjectConversation,
  currentUserId,
}) => {
  const { projects, loading, error, refresh } = usePaProjects(true);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    setCreateError(null);
    try {
      const created = await projectsApi.create({
        name,
        description: newDescription.trim() || undefined,
      });
      if (created) {
        setCreating(false);
        setNewName('');
        setNewDescription('');
        await refresh();
        setOpenProjectId(created.id);
      } else {
        setCreateError('Creazione non riuscita');
      }
    } catch (err) {
      console.error('[ProjectsPanel] create failed', err);
      setCreateError('Creazione non riuscita');
    } finally {
      setSaving(false);
    }
  };

  if (openProjectId) {
    return (
      <ProjectDetailPanel
        projectId={openProjectId}
        currentUserId={currentUserId}
        onBack={() => { setOpenProjectId(null); void refresh(); }}
        onClosed={() => { setOpenProjectId(null); void refresh(); }}
        onSelectConversation={onSelectConversation}
        onNewProjectConversation={onNewProjectConversation}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="px-3 py-2 flex items-center justify-between bg-card border-b border-border shrink-0">
        <span className="text-[11px] font-semibold uppercase text-muted-foreground">Progetti</span>
        {creating ? (
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => { setCreating(false); setCreateError(null); }}>
            <X className="h-3 w-3 mr-0.5" /> Annulla
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setCreating(true)}
            title="Nuovo progetto"
            aria-label="Nuovo progetto"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {creating && (
        <div className="px-3 py-2 border-b border-border bg-card space-y-1.5 shrink-0">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
            placeholder="Nome del progetto"
            maxLength={200}
            autoFocus
            className="w-full h-7 px-2 rounded border border-border bg-background text-xs focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Descrizione (facoltativa)"
            rows={2}
            className="w-full px-2 py-1 rounded border border-border bg-background text-xs resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400"
          />
          {createError && <div className="text-[10px] text-red-600">{createError}</div>}
          <Button
            size="sm"
            className="h-7 w-full text-xs bg-violet-600 hover:bg-violet-700 text-white"
            disabled={!newName.trim() || saving}
            onClick={() => void handleCreate()}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Crea progetto'}
          </Button>
        </div>
      )}

      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div className="px-1 py-1">
          {loading && projects.length === 0 && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && <div className="text-xs text-red-600 px-2 py-2">{error}</div>}
          {!loading && !error && projects.length === 0 && !creating && (
            <div className="text-xs text-muted-foreground px-2 py-4 text-center space-y-1">
              <FolderKanban className="h-5 w-5 mx-auto text-muted-foreground/60" />
              <div>Nessun progetto</div>
              <div className="text-[10px]">Un progetto condivide istruzioni, conoscenza e skill con tutte le sue conversazioni.</div>
            </div>
          )}
          <ul>
            {projects.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setOpenProjectId(p.id)}
                  className="w-full flex items-start gap-2 px-2 py-1.5 rounded text-left text-xs text-foreground hover:bg-muted"
                >
                  <FolderKanban className="h-3.5 w-3.5 shrink-0 mt-0.5 text-violet-500" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate" title={p.name}>{p.name}</span>
                    <span className="block text-[10px] text-muted-foreground truncate">
                      {p.conversationCount} conv. · {p.memberCount} <Users className="inline h-2.5 w-2.5" />
                      {p.updatedAt ? ` · ${formatRelativeTime(p.updatedAt)}` : ''}
                      {p.myRole === 'owner' ? ' · proprietario' : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </ScrollArea>
    </div>
  );
};
